/**
 * View-model adapter for /searchHotelsComposite hotel_results rows.
 * Keeps list UI off raw API shapes.
 */

/**
 * @param {object} hotelResult — one element from API hotel_results[]
 * @param {number} totalRooms
 * @returns {object}
 */
export function mapCompositeHotelResultToVM(hotelResult, totalRooms) {
  const quotes = Array.isArray(hotelResult?.room_quotes)
    ? hotelResult.room_quotes
    : [];
  const roomRows = quotes.map((q) => ({
    roomIndex: q.room_index,
    status: q.status,
    roomName: q.room_name,
    price: q.price,
    board: q.board,
    cancellation: q.cancellation,
    quoteToken: q.quote_token,
    packageId: q.package_id,
    roomRequest: q.room_request,
    reason: q.reason,
  }));

  const availability = hotelResult?.availability_status || "failed";
  const pricedCount = roomRows.filter((r) => r.status === "ok").length;

  return {
    totalPrice: hotelResult?.total_price ?? null,
    roomRows,
    availabilityBadge: availability,
    progressText: `${pricedCount}/${totalRooms}`,
    isPartial: availability === "partial",
    isComplete: availability === "complete",
    isFailed: availability === "failed",
    canBook: hotelResult?.can_book === true,
    missingRooms: Array.isArray(hotelResult?.missing_rooms)
      ? hotelResult.missing_rooms
      : [],
    warnings: Array.isArray(hotelResult?.warnings) ? hotelResult.warnings : [],
  };
}

/**
 * Legacy-shaped rows for existing SearchContext pipeline (dedup, distance, commission).
 * @param {object[]} hotelResults
 * @param {number} totalRooms
 * @returns {object[]}
 */
/**
 * Payload for POST /validateCompositeHotelQuote (composite split search only).
 */
export function buildCompositeValidatePayload(item, phrase, sessionId, meta) {
  const raw = item?._compositeRaw;
  if (!raw?.room_quotes?.length || !sessionId || !phrase) return null;
  return {
    hotel_id: String(raw.hotel_id ?? ""),
    supplier_id: String(raw.supplier_id ?? ""),
    search_request_id: meta?.search_request_id ?? null,
    session: sessionId,
    checkIn: phrase.checkIn,
    checkOut: phrase.checkOut,
    currency: phrase.currency ?? "ILS",
    lon: phrase.lon,
    lat: phrase.lat,
    dynamic: phrase.dynamic ?? false,
    room_quotes: raw.room_quotes,
  };
}

/** Sum current_price from validation room_validation (USD supplier units). */
export function sumSuggestedPriceFromRoomValidation(roomValidation) {
  if (!Array.isArray(roomValidation)) return null;
  let sum = 0;
  let any = false;
  for (const row of roomValidation) {
    if (row?.current_price != null && !Number.isNaN(Number(row.current_price))) {
      sum += Number(row.current_price);
      any = true;
    }
  }
  return any ? sum : null;
}

export function mapCompositeHotelResultsToLegacyRows(hotelResults, totalRooms) {
  if (!Array.isArray(hotelResults)) return [];
  return hotelResults.map((hr) => {
    const id = hr.hotel_id != null ? String(hr.hotel_id) : "";
    const vm = mapCompositeHotelResultToVM(hr, totalRooms || 0);
    const total = vm.totalPrice;
    return {
      ID: id,
      Id: id,
      City: hr.hotel_meta?.City ?? null,
      Supplier: hr.supplier_id ?? "",
      DisplayName: hr.hotel_name || "",
      Name: hr.hotel_name || "",
      LowestPackagePrice:
        total != null && !Number.isNaN(Number(total)) ? Number(total) : 0,
      StarRating: hr.hotel_meta?.StarRating,
      DefaultImage: hr.hotel_meta?.DefaultImage,
      GeoLocation: null,
      _compositeHotelSearch: vm,
      _compositeRaw: hr,
      isInternalHotel: hr.isInternalHotel,
      isPinnedByHotelsRiver: hr.isPinnedByHotelsRiver,
      pinnedBadgeText: hr.pinnedBadgeText,
      pinnedCampaignDestinationCode: hr.pinnedCampaignDestinationCode ?? null,
      pinnedDisplayOrder: hr.pinnedDisplayOrder,
      _hr_sort_index: hr._hr_sort_index,
    };
  });
}
